import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ATTORNEY } from "@/data/content";

const FORMSPREE = process.env.REACT_APP_FORMSPREE_ENDPOINT || "";

export default function Contact() {
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        const form = e.currentTarget;
        const data = new FormData(form);

        if (!FORMSPREE || FORMSPREE.includes("YOUR_FORM_ID")) {
            toast.error(
                "Form endpoint not configured. Set REACT_APP_FORMSPREE_ENDPOINT in /app/frontend/.env",
            );
            return;
        }

        setSending(true);
        try {
            const res = await fetch(FORMSPREE, {
                method: "POST",
                body: data,
                headers: { Accept: "application/json" },
            });
            if (res.ok) {
                setSent(true);
                form.reset();
                toast.success("Thanks — Joe will be in touch shortly.");
            } else {
                const j = await res.json().catch(() => ({}));
                toast.error(
                    j?.errors?.[0]?.message ||
                        "Something went wrong. Please call (916) 492-2000.",
                );
            }
        } catch (err) {
            toast.error(
                "Network error. Please try again or call (916) 492-2000.",
            );
        } finally {
            setSending(false);
        }
    }

    return (
        <>
            <SEO
                title="Contact Joseph G. Helfrick — Free Consultation"
                description="Request a free, confidential consultation with Sacramento personal injury attorney Joseph G. Helfrick. Call (916) 492-2000 or send the secure intake form. No fee unless we win."
                path="/contact"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Contact", path: "/contact" },
                ]}
            />

            {/* HERO */}
            <section
                data-testid="contact-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            Contact
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            Tell us what happened.{" "}
                            <span className="italic text-[#D4AF37]">
                                The first call is free.
                            </span>
                        </h1>
                        <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                            Every conversation with our office is free and
                            strictly confidential. There is no obligation, and
                            you owe us nothing unless we recover money for you.
                        </p>
                    </div>
                </div>
            </section>

            {/* CONTACT GRID */}
            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
                    {/* Info column */}
                    <aside className="lg:col-span-4 space-y-8">
                        <div data-testid="contact-info-phone">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold flex items-center gap-3">
                                <Phone size={14} />
                                Call
                            </div>
                            <a
                                href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                className="mt-3 block font-serif text-3xl text-[#F7F5EB] hover:text-[#D4AF37] transition-colors"
                            >
                                {ATTORNEY.phone}
                            </a>
                            <div className="text-xs text-[#9FB3D4] mt-2">
                                Mon–Fri · 8:30am – 5:30pm PT (after-hours
                                voicemail returned next business day)
                            </div>
                        </div>

                        <div data-testid="contact-info-email">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold flex items-center gap-3">
                                <Mail size={14} />
                                Email
                            </div>
                            <a
                                href={`mailto:${ATTORNEY.email}`}
                                className="mt-3 block font-serif text-xl text-[#F7F5EB] hover:text-[#D4AF37] transition-colors break-all"
                            >
                                {ATTORNEY.email}
                            </a>
                        </div>

                        <div data-testid="contact-info-office">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold flex items-center gap-3">
                                <MapPin size={14} />
                                Office
                            </div>
                            <div className="mt-3 text-[#F7F5EB] leading-relaxed">
                                {ATTORNEY.firm}
                                <br />
                                {ATTORNEY.address.street}
                                <br />
                                {ATTORNEY.address.city},{" "}
                                {ATTORNEY.address.region}{" "}
                                {ATTORNEY.address.postal}
                            </div>
                        </div>

                        <div className="bg-[#11203D] border border-[#1F3458] p-6">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold">
                                Why people call us
                            </div>
                            <ul className="mt-4 space-y-2 text-sm text-[#9FB3D4]">
                                <li>— Free, no-pressure consultation</li>
                                <li>— No fee unless we recover money</li>
                                <li>— Direct attorney contact, not a screener</li>
                                <li>
                                    — Sacramento, CA, and surrounding areas trial
                                    experience
                                </li>
                            </ul>
                        </div>

                        <div className="border border-[#1F3458] p-6">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold">
                                Prefer a guided start?
                            </div>
                            <p className="mt-3 text-sm text-[#9FB3D4] leading-relaxed">
                                Answer four quick questions and your case
                                review reaches Joe with the details he needs
                                already organized.
                            </p>
                            <Link
                                to="/case-evaluator"
                                data-testid="contact-evaluator-link"
                                className="mt-4 inline-flex items-center gap-2 text-[#D4AF37] text-sm font-bold uppercase tracking-wider hover:text-[#B8962A] transition-colors"
                            >
                                60-Second Case Evaluator
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </aside>

                    {/* Form column */}
                    <div className="lg:col-span-8">
                        <div className="bg-[#11203D] border border-[#1F3458] p-8 md:p-12">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold">
                                Free Case Review
                            </div>
                            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight">
                                Send us a few details about your case.
                            </h2>

                            {sent ? (
                                <div
                                    data-testid="contact-success"
                                    className="mt-10 border border-[#D4AF37] bg-[#0B1A36] p-8"
                                >
                                    <div className="font-serif text-2xl text-[#D4AF37]">
                                        Message received.
                                    </div>
                                    <p className="mt-3 text-[#F7F5EB]/90">
                        Joe will review your message and reach
                                        out within one business day. If your
                                        matter is urgent, please call{" "}
                                        <a
                                            href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                            className="gold-link"
                                        >
                                            {ATTORNEY.phone}
                                        </a>
                                        .
                                    </p>
                                </div>
                            ) : (
                                <form
                                    data-testid="contact-form"
                                    onSubmit={onSubmit}
                                    className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5"
                                >
                                    <div className="md:col-span-1">
                                        <label
                                            htmlFor="firstName"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            First Name *
                                        </label>
                                        <input
                                            id="firstName"
                                            name="firstName"
                                            required
                                            data-testid="contact-first-name"
                                            className="w-full px-4 py-3 text-base"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label
                                            htmlFor="lastName"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Last Name *
                                        </label>
                                        <input
                                            id="lastName"
                                            name="lastName"
                                            required
                                            data-testid="contact-last-name"
                                            className="w-full px-4 py-3 text-base"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label
                                            htmlFor="email"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Email *
                                        </label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            required
                                            data-testid="contact-email"
                                            className="w-full px-4 py-3 text-base"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label
                                            htmlFor="phone"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Phone *
                                        </label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            required
                                            data-testid="contact-phone"
                                            className="w-full px-4 py-3 text-base"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label
                                            htmlFor="caseType"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Type of Case
                                        </label>
                                        <select
                                            id="caseType"
                                            name="caseType"
                                            data-testid="contact-case-type"
                                            className="w-full px-4 py-3 text-base"
                                            defaultValue=""
                                        >
                                            <option value="" disabled>
                                                Select a case type…
                                            </option>
                                            <option>
                                                Car or Truck Accident
                                            </option>
                                            <option>Slip / Trip & Fall</option>
                                            <option>
                                                Medical or Dental Malpractice
                                            </option>
                                            <option>
                                                Dog Bite / Animal Attack
                                            </option>
                                            <option>
                                                Workplace Accident
                                            </option>
                                            <option>
                                                Ski Accident
                                            </option>
                                            <option>
                                                Government Tort Claim
                                            </option>
                                            <option>Property Dispute</option>
                                            <option>Other / Not sure</option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label
                                            htmlFor="incidentDate"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Approximate Date of Incident
                                        </label>
                                        <input
                                            id="incidentDate"
                                            name="incidentDate"
                                            type="date"
                                            data-testid="contact-incident-date"
                                            className="w-full px-4 py-3 text-base"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label
                                            htmlFor="message"
                                            className="block text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2"
                                        >
                                            Briefly describe what happened *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={6}
                                            required
                                            data-testid="contact-message"
                                            className="w-full px-4 py-3 text-base resize-y"
                                            placeholder="Tell us where it happened, what injuries you sustained, and whether you've spoken to insurance yet."
                                        />
                                    </div>

                                    {/* Honeypot for spam */}
                                    <input
                                        type="text"
                                        name="_gotcha"
                                        tabIndex="-1"
                                        autoComplete="off"
                                        style={{ display: "none" }}
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="hidden"
                                        name="_subject"
                                        value="New free case review — JoeHelfrick.law"
                                    />

                                    <div className="md:col-span-2 flex flex-col gap-4 mt-2">
                                        <p className="text-xs text-[#9FB3D4] leading-relaxed">
                                            Submitting this form does not create
                                            an attorney-client relationship.
                                            Please do not include confidential
                                            information until we have agreed in
                                            writing to represent you.
                                        </p>
                                        <button
                                            type="submit"
                                            disabled={sending}
                                            data-testid="contact-submit"
                                            className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors disabled:opacity-60 w-fit"
                                        >
                                            {sending ? (
                                                <>
                                                    <Loader2
                                                        size={16}
                                                        className="animate-spin"
                                                    />
                                                    Sending…
                                                </>
                                            ) : (
                                                <>
                                                    Send Free Case Review{" "}
                                                    <ArrowRight size={16} />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
